// PATH: src/pages/admin/tabs/VersesTab.jsx
// VERSION: 1.2 — Auto-relleno desde Bible SuperSearch + guardar con <sup>n</sup> + CRUD (PUT)

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Flex, Heading, Input, Table, Tbody, Td, Th, Thead, Tr, Textarea, useToast,
  Select, HStack, Switch, Text, Badge, Spinner, IconButton, useBreakpointValue, useColorModeValue,
} from '@chakra-ui/react';
import { FiSave, FiTrash2, FiRefreshCw, FiEdit2, FiSearch } from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';
import booksES from '../../../data/books_es.json';

/* ─────────────────────────────────────────────────────────
   Config Bible SuperSearch (sin clave, con CORS)
   Doc: https://api.biblesupersearch.com
   ───────────────────────────────────────────────────────── */
const BSS_API = 'https://api.biblesupersearch.com/api';

const VERSIONS = [
  { value: 'rv_1909', label: 'Reina-Valera 1909' },
  { value: 'rv_1909_strongs', label: 'RV1909 + Strong’s' },
  { value: 'rv_1858', label: 'Reina-Valera 1858 (NT)' },
  { value: 'sagradas', label: 'Sagradas Escrituras 1569' },
  { value: 'rvg', label: 'Reina-Valera Gómez 2010' },
];

// Capítulos por libro (slug en español)
const CHAPTERS_BY_SLUG = {
  genesis: 50, exodo: 40, levitico: 27, numeros: 36, deuteronomio: 34,
  josue: 24, jueces: 21, rut: 4, '1-samuel': 31, '2-samuel': 24,
  '1-reyes': 22, '2-reyes': 25, '1-cronicas': 29, '2-cronicas': 36,
  esdras: 10, nehemias: 13, ester: 10, job: 42, salmos: 150,
  proverbios: 31, eclesiastes: 12, cantares: 8, isaias: 66, jeremias: 52,
  lamentaciones: 5, ezequiel: 48, daniel: 12, oseas: 14, joel: 3, amos: 9,
  abdias: 1, jonas: 4, miqueas: 7, nahum: 3, habacuc: 3, sofonias: 3,
  hageo: 2, zacarias: 14, malaquias: 4,
  mateo: 28, marcos: 16, lucas: 24, juan: 21, hechos: 28, romanos: 16,
  '1-corintios': 16, '2-corintios': 13, galatas: 6, efesios: 6,
  filipenses: 4, colosenses: 4, '1-tesalonicenses': 5, '2-tesalonicenses': 3,
  '1-timoteo': 6, '2-timoteo': 4, tito: 3, filemon: 1, hebreos: 13,
  santiago: 5, '1-pedro': 5, '2-pedro': 3, '1-juan': 5, '2-juan': 1,
  '3-juan': 1, judas: 1, apocalipsis: 22,
};

// Mapa ES->EN para el API
const ENG_BY_SLUG = {
  genesis: 'Genesis', exodo: 'Exodus', levitico: 'Leviticus', numeros: 'Numbers', deuteronomio: 'Deuteronomy',
  josue: 'Joshua', jueces: 'Judges', rut: 'Ruth', '1-samuel': '1 Samuel', '2-samuel': '2 Samuel',
  '1-reyes': '1 Kings', '2-reyes': '2 Kings', '1-cronicas': '1 Chronicles', '2-cronicas': '2 Chronicles',
  esdras: 'Ezra', nehemias: 'Nehemiah', ester: 'Esther', job: 'Job', salmos: 'Psalms',
  proverbios: 'Proverbs', eclesiastes: 'Ecclesiastes', cantares: 'Song of Solomon',
  isaias: 'Isaiah', jeremias: 'Jeremiah', lamentaciones: 'Lamentations', ezequiel: 'Ezekiel', daniel: 'Daniel',
  oseas: 'Hosea', joel: 'Joel', amos: 'Amos', abdias: 'Obadiah', jonas: 'Jonah',
  miqueas: 'Micah', nahum: 'Nahum', habacuc: 'Habakkuk', sofonias: 'Zephaniah', hageo: 'Haggai',
  zacarias: 'Zechariah', malaquias: 'Malachi',
  mateo: 'Matthew', marcos: 'Mark', lucas: 'Luke', juan: 'John', hechos: 'Acts', romanos: 'Romans',
  '1-corintios': '1 Corinthians', '2-corintios': '2 Corinthians', galatas: 'Galatians', efesios: 'Ephesians',
  filipenses: 'Philippians', colosenses: 'Colossians', '1-tesalonicenses': '1 Thessalonians', '2-tesalonicenses': '2 Thessalonians',
  '1-timoteo': '1 Timothy', '2-timoteo': '2 Timothy', tito: 'Titus', filemon: 'Philemon', hebreos: 'Hebrews',
  santiago: 'James', '1-pedro': '1 Peter', '2-pedro': '2 Peter', '1-juan': '1 John', '2-juan': '2 John',
  '3-juan': '3 John', judas: 'Jude', apocalipsis: 'Revelation',
};



/* Helpers de API */
async function bssFetch(params) {
  const url = `${BSS_API}?${new URLSearchParams(params).toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (Array.isArray(json?.errors) && json.errors.length) {
    throw new Error(json.errors.join(' | '));
  }
  return json;
}

async function fetchChapterCount({ bible, engBook, chapter }) {
  const data = await bssFetch({
    bible,
    reference: `${engBook} ${chapter}`,
    data_format: 'passage',
    page_all: 'true',
  });
  const first = Array.isArray(data?.results) ? data.results[0] : null;
  return Number(first?.verses_count || 0);
}

async function fetchPassage({ bible, engBook, chapter, vFrom, vTo }) {
  const hasRange = vTo && Number(vTo) >= Number(vFrom);
  const ref = hasRange ? `${engBook} ${chapter}:${vFrom}-${vTo}` : `${engBook} ${chapter}:${vFrom}`;
  const data = await bssFetch({
    bible, reference: ref, data_format: 'passage', page_all: 'true',
  });
  const first = Array.isArray(data?.results) ? data.results[0] : null;
  const versesByBible = first?.verses?.[bible] || {};
  const versesOfChapter = versesByBible?.[String(chapter)] || {};
  const verseNums = Object.keys(versesOfChapter).map(n => Number(n)).sort((a, b) => a - b);
  return verseNums.map(n => ({
    verse: n,
    text: String(versesOfChapter[String(n)]?.text ?? ''),
  }));
}

/* Parser simple: "Libro 3:16-18" -> { slug, chapter, vFrom, vTo } */
function parseReference(ref) {
  if (!ref) return null;
  const m = ref.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/i);
  if (!m) return null;
  const [, bookName, ch, vf, vt] = m;
  const found = booksES.items.find(b => b.name.toLowerCase() === bookName.toLowerCase());
  if (!found) return null;
  return {
    slug: found.slug,
    chapter: Number(ch),
    vFrom: Number(vf),
    vTo: vt ? Number(vt) : Number(vf),
  };
}

// Escapar HTML para texto antes de envolver con <sup>
function escapeHtml(s = '') {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function VersesTab() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const thColor = useColorModeValue('gray.700', 'white');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const rowColor = useColorModeValue('inherit', 'text.default');
  const emptyColor = useColorModeValue('inherit', 'text.muted');

  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const labelColor = useColorModeValue('gray.700', 'text.muted');
  const previewBorderColor = useColorModeValue('brand.300', 'border.default');
  const previewBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const previewTextColor = useColorModeValue('gray.800', 'text.default');

  // Estilos dinámicos "bw"
  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'whiteAlpha.50'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'gray.500'), fontWeight: 'semibold' },
      _hover: { borderColor: 'brand.400' },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: { bg: 'accent.600', borderColor: 'accent.600', transform: 'translateY(-1px)', boxShadow: 'md' },
      _active: { transform: 'translateY(0)', boxShadow: 'sm' },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'transparent'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: { bg: useColorModeValue('brand.50', 'whiteAlpha.100'), borderColor: 'brand.400' },
    },
  };


  // Selección (como Biblia)
  const [version, setVersion] = useState('rv_1909');
  const [bookSlug, setBookSlug] = useState('juan');
  const [chapter, setChapter] = useState(3);
  const [verseCount, setVerseCount] = useState(0);
  const [vFrom, setVFrom] = useState(16);
  const [vTo, setVTo] = useState(18);
  const [autoFill, setAutoFill] = useState(true);
  const [autoLoading, setAutoLoading] = useState(false);
  const [previewVerses, setPreviewVerses] = useState([]); // [{verse, text}]
  const [saveWithSupers, setSaveWithSupers] = useState(true);
  const debounceRef = useRef(null);

  // Form CRUD
  const [form, setForm] = useState({ id: null, reference: '', text: '' });

  // Carga inicial
  async function loadList() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/verses', { params: { limit: 1000, offset: 0 } });
      setList(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
    } catch {
      toast({ status: 'error', title: 'Error cargando versículos' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadList(); }, []); // eslint-disable-line

  // Libros OT/NT
  const idxNT = useMemo(() => booksES.items.findIndex(b => b.slug === 'mateo'), []);
  const booksOT = useMemo(() => booksES.items.slice(0, idxNT < 0 ? booksES.items.length : idxNT), [idxNT]);
  const booksNT = useMemo(() => booksES.items.slice(idxNT < 0 ? booksES.items.length : idxNT), [idxNT]);

  const chapterMax = useMemo(() => CHAPTERS_BY_SLUG[bookSlug] ?? 1, [bookSlug]);
  const chapterOptions = useMemo(() => Array.from({ length: chapterMax }, (_, i) => i + 1), [chapterMax]);
  const verseOptions = useMemo(
    () => verseCount > 0 ? Array.from({ length: verseCount }, (_, i) => i + 1) : [],
    [verseCount]
  );
  const bookName = useMemo(() => booksES.items.find(x => x.slug === bookSlug)?.name ?? 'Juan', [bookSlug]);
  const engBook = useMemo(() => ENG_BY_SLUG[bookSlug] ?? 'John', [bookSlug]);

  // Referencia visible (y para el form)
  const passageRef = useMemo(() => {
    const hasRange = vFrom && vTo && Number(vTo) >= Number(vFrom);
    return `${bookName} ${chapter}:${vFrom || ''}${hasRange && vTo ? '-' + vTo : ''}`.replace(/\s+/g, ' ').trim();
  }, [bookName, chapter, vFrom, vTo]);

  // Mantener form.reference sincronizado con la selección
  useEffect(() => {
    setForm(f => ({ ...f, reference: passageRef }));
  }, [passageRef]);

  // Cargar conteo de versículos al cambiar libro/capítulo/versión
  const loadVerseCount = useCallback(async () => {
    if (!engBook || !chapter) return;
    try {
      const count = await fetchChapterCount({ bible: version, engBook, chapter: Number(chapter) });
      setVerseCount(count || 0);
      if (count > 0) {
        setVFrom(prev => Math.min(Math.max(1, prev || 1), count));
        setVTo(prev => {
          const safe = Math.min(Math.max(1, prev || 1), count);
          return safe < (vFrom || 1) ? (vFrom || 1) : safe;
        });
      } else {
        setVFrom(1);
        setVTo(1);
      }
    } catch {
      setVerseCount(0);
      setVFrom(1);
      setVTo(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, engBook, chapter, vFrom]);

  useEffect(() => {
    loadVerseCount();
  }, [version, bookSlug, chapter, loadVerseCount]);

  // Auto-relleno: debounce al modificar selección
  const doAutoFill = useCallback(async () => {
    if (!engBook || !chapter || !vFrom) return;
    try {
      setAutoLoading(true);
      const verses = await fetchPassage({
        bible: version,
        engBook,
        chapter: Number(chapter) || 1,
        vFrom: Number(vFrom) || 1,
        vTo: Number(vTo) || Number(vFrom) || 1,
      });
      setPreviewVerses(verses);
      const joined = verses.map(v => v.text.trim()).join(' ');
      setForm(f => ({ ...f, text: joined }));
    } catch (e) {
      toast({ status: 'error', title: 'No se pudo auto-rellenar', description: e?.message?.slice(0, 140) });
    } finally {
      setAutoLoading(false);
    }
  }, [version, engBook, chapter, vFrom, vTo, toast]);

  useEffect(() => {
    if (!autoFill) return; // desactivado
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { doAutoFill(); }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [autoFill, version, bookSlug, chapter, vFrom, vTo, doAutoFill]);

  // Handlers selección
  const onBook = (slug) => { setBookSlug(slug); setChapter(1); setVFrom(1); setVTo(1); };
  const onChapter = (n) => { const num = Number(n) || 1; setChapter(num); setVFrom(1); setVTo(1); };
  const onVFrom = (n) => {
    const from = Math.max(1, Math.min(Number(n) || 1, verseCount || 1));
    setVFrom(from);
    setVTo(prev => Math.max(from, Math.min(prev || from, verseCount || from)));
  };
  const onVTo = (n) => {
    const to = Math.max(1, Math.min(Number(n) || 1, verseCount || 1));
    setVTo(Math.max(vFrom || 1, to));
  };

  // CRUD
  async function save() {
    const payload = {
      reference: (form.reference || '').trim(),
      text: (form.text || '').trim(),
    };
    if (!payload.reference || !payload.text) {
      toast({ status: 'warning', title: 'Referencia y texto son requeridos' });
      return;
    }
    try {
      // Si está activo "Guardar con superíndices", construimos el HTML <sup>n</sup> + texto escapado
      if (saveWithSupers) {
        let versesToUse = previewVerses;
        // Si no hay vista previa (autoFill OFF), intentar fetch por referencia
        if (!versesToUse.length) {
          const parsed = parseReference(payload.reference);
          if (parsed) {
            const engB = ENG_BY_SLUG[parsed.slug] || 'John';
            versesToUse = await fetchPassage({
              bible: version,
              engBook: engB,
              chapter: parsed.chapter,
              vFrom: parsed.vFrom,
              vTo: parsed.vTo,
            });
          }
        }
        if (versesToUse.length) {
          payload.text = versesToUse
            .map(v => `<sup>${v.verse}</sup> ${escapeHtml(v.text)}`)
            .join(' ')
            .trim();
        }
      }

      if (form.id) {
        // Backend actual tiene PUT (no PATCH).
        await axiosInstance.put(`/verses/${form.id}`, payload);
        toast({ status: 'success', title: 'Versículo actualizado' });
      } else {
        await axiosInstance.post('/verses', payload);
        toast({ status: 'success', title: 'Versículo creado' });
      }
      setForm({ id: null, reference: passageRef, text: '' });
      loadList();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  function editRow(r) {
    setForm({ id: r.id, reference: r.reference || '', text: r.text || '' });
    // Intentar parsear referencia para precargar selección
    const parsed = parseReference(r.reference || '');
    if (parsed) {
      setBookSlug(parsed.slug);
      setChapter(parsed.chapter);
      setVFrom(parsed.vFrom);
      setVTo(parsed.vTo);
      // regenerar vista previa
      setTimeout(() => { doAutoFill(); }, 0);
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar versículo?')) return;
    try {
      await axiosInstance.delete(`/verses/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      loadList();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  function resetForm() {
    setForm({ id: null, reference: passageRef, text: '' });
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>Versículos (admin)</Heading>
      <Text fontSize="sm" mb={4} color={subTextColor}>
        Selecciona un pasaje y el <b>Texto</b> se auto-rellenará desde Bible SuperSearch. Puedes desactivar el auto-relleno si prefieres.
      </Text>

      {/* Toolbar de selección (como Biblia) */}
      <Flex gap={3} wrap="wrap" align="flex-end" mb={3}>
        <Box w={{ base: '100%', md: 'auto' }}>
          <Text fontSize="sm" color={labelColor} mb={1}>Versión</Text>
          <Select value={version} onChange={(e) => setVersion(e.target.value)} w="full" maxW={{ base: '100%', md: '260px' }} {...bw.input}>
            {VERSIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </Select>
        </Box>

        <Box w={{ base: '100%', md: 'auto' }}>
          <Text fontSize="sm" color={labelColor} mb={1}>Libro</Text>
          <Select value={bookSlug} onChange={(e) => onBook(e.target.value)} w="full" maxW={{ base: '100%', md: '220px' }} {...bw.input}>
            <optgroup label="Antiguo Testamento">
              {booksOT.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </optgroup>
            <optgroup label="Nuevo Testamento">
              {booksNT.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </optgroup>
          </Select>
        </Box>

        <Box w={{ base: '100%', md: 'auto' }}>
          <Text fontSize="sm" color={labelColor} mb={1}>Capítulo</Text>
          <Select value={chapter} onChange={(e) => onChapter(e.target.value)} w="full" maxW={{ base: '100%', md: '120px' }} {...bw.input}>
            {chapterOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
        </Box>

        <Box w={{ base: '100%', md: 'auto' }}>
          <Text fontSize="sm" color={labelColor} mb={1}>Desde</Text>
          <Select value={vFrom} onChange={(e) => onVFrom(e.target.value)} w="full" maxW={{ base: '100%', md: '110px' }} {...bw.input} isDisabled={verseOptions.length === 0}>
            {verseOptions.map(n => <option key={`vf-${n}`} value={n}>{n}</option>)}
          </Select>
        </Box>

        <Box w={{ base: '100%', md: 'auto' }}>
          <Text fontSize="sm" color={labelColor} mb={1}>Hasta</Text>
          <Select value={vTo} onChange={(e) => onVTo(e.target.value)} w="full" maxW={{ base: '100%', md: '110px' }} {...bw.input} isDisabled={verseOptions.length === 0}>
            {verseOptions.filter(n => n >= (vFrom || 1)).map(n => <option key={`vt-${n}`} value={n}>{n}</option>)}
          </Select>
        </Box>



        <Flex
          ml="auto"
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'center' }}
          gap={{ base: 2, md: 5 }}
        >
          <HStack>
            <Switch isChecked={autoFill} onChange={(e) => setAutoFill(e.target.checked)} />
            <Text fontSize="sm" color={labelColor}>Auto-rellenar</Text>
          </HStack>
          <HStack>
            <Switch isChecked={saveWithSupers} onChange={(e) => setSaveWithSupers(e.target.checked)} />
            <Text fontSize="sm" color={labelColor}>Guardar con superíndices</Text>
          </HStack>
          <HStack>
            <IconButton
              aria-label="Rellenar ahora"
              icon={autoLoading ? <Spinner size="sm" /> : <FiSearch />}
              onClick={doAutoFill}
              title="Rellenar ahora"
              variant="outline"
            />
            <Badge variant="subtle" colorScheme="blue">{String(version).toUpperCase()}</Badge>
          </HStack>
        </Flex>
      </Flex>

      {/* Form */}
      <Flex direction="column" gap={3} mb={4}>
        <Input
          {...bw.input}
          placeholder={useBreakpointValue({
            base: 'Buscar…',
            md: 'Referencia (e.g., Juan 3:16-18)',
          })}
          value={form.reference}
          onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
        />
        <Textarea
          {...bw.input}
          rows={5}
          placeholder="Texto del pasaje"
          value={form.text}
          onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
        />
        {/* Vista previa con superíndices */}
        {
          previewVerses.length > 0 && (
            <Box
              border="1px dashed"
              borderColor={previewBorderColor}
              borderRadius="md"
              p={3}
              bg={previewBg}
            >
              {previewVerses.map((v, i) => (
                <Text key={`${v.verse}-${i}`} color={previewTextColor} lineHeight="tall">
                  <sup style={{ opacity: 0.75, marginRight: 6 }}>{v.verse}</sup>
                  {v.text}
                </Text>
              ))}
            </Box>
          )
        }
        <HStack gap={2} wrap="wrap">
          <Button {...bw.btn} leftIcon={<FiSave />} onClick={save}>
            {form.id ? 'Actualizar' : 'Crear'}
          </Button>
          {form.id && (
            <Button {...bw.btnGhost} onClick={resetForm}>
              Cancelar
            </Button>
          )}
          <Button {...bw.btnGhost} leftIcon={<FiRefreshCw />} onClick={loadList}>
            Refrescar lista
          </Button>
        </HStack>
      </Flex >

      {/* Tabla */}
      < Box overflowX="auto" >
        <Table
          size="sm"
          variant="simple"
          sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
          border="1px solid"
          borderColor="brand.200"
        >
          <Thead bg={theadBg}>
            <Tr>
              <Th borderColor="brand.200" color={thColor}>ID</Th>
              <Th borderColor="brand.200" color={thColor}>Referencia</Th>
              <Th borderColor="brand.200" color={thColor}>Texto</Th>
              <Th borderColor="brand.200" color={thColor}>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {list.map((v) => (
              <Tr key={v.id} _hover={{ bg: rowHoverBg }} color={rowColor}>
                <Td borderColor="brand.200" whiteSpace="nowrap">{v.id}</Td>
                <Td borderColor="brand.200" minW="220px">{v.reference}</Td>
                <Td borderColor="brand.200" maxW="640px" isTruncated title={v.text}>{v.text}</Td>
                <Td borderColor="brand.200" whiteSpace="nowrap">
                  <Button {...bw.btnGhost} size="xs" mr={2} leftIcon={<FiEdit2 />} onClick={() => editRow(v)}>
                    Editar
                  </Button>
                  <Button {...bw.btnGhost} size="xs" leftIcon={<FiTrash2 />} onClick={() => remove(v.id)}>
                    Eliminar
                  </Button>
                </Td>
              </Tr>
            ))}
            {list.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" py={4} borderColor="brand.200" color={emptyColor}>
                  No hay versículos registrados.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box >
    </Box >
  );
}
